import baseAPI from "@/api/base";
import { EditTemplate, GetTemplate, RegisterTemplate } from "@/dtos/Template.dto";
import {
  CHECK_TEMPLATE_SHORTCUT_URL,
  COPY_TEMPLATE_URL,
  DELETE_TEMPLATE_URL,
  EDIT_TEMPLATE_URL,
  GET_TEMPLATE_URL,
  GET_TEMPLATE_VARIABLES_URL,
  NEGATIVE_TEMPLATE_URL,
  POSITIVE_TEMPLATE_URL,
  REGISTER_TEMPLATE_URL,
  TOGGLE_TEMPLATE_IS_ACTIVE_URL,
  UPLOAD_TEMPLATE_ATTACHMENT_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";

class Template {
  static getTemplates = async (
    getTemplate: GetTemplate,
    retryCount = 0,
    maxRetries = 2
  ): Promise<AxiosResponse> => {
    try {
      const response: AxiosResponse = await baseAPI.get(GET_TEMPLATE_URL, {
        params: getTemplate,
        timeout: 15000,
      });
      return response;
    } catch (error: any) {
      console.error("Template request failed:", error);

      if (
        (error.code === "ECONNABORTED" || error.message?.includes("timeout")) &&
        retryCount < maxRetries
      ) {
        const delay = Math.pow(2, retryCount) * 1000;

        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.getTemplates(getTemplate, retryCount + 1, maxRetries);
      }

      throw error;
    }
  };

  static registerTemplate = async (registerTemplate: RegisterTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(REGISTER_TEMPLATE_URL, {
      ...registerTemplate,
    });
    return response;
  };

  static editTemplate = async (editTemplate: EditTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(EDIT_TEMPLATE_URL, {
      ...editTemplate,
    });
    return response;
  };

  static copyTemplate = async (editTemplate: EditTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(COPY_TEMPLATE_URL, {
      ...editTemplate,
    });
    return response;
  };

  static positiveReply = async (editTemplate: EditTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(POSITIVE_TEMPLATE_URL, {
      ...editTemplate,
    });
    return response;
  };

  static negativeReply = async (editTemplate: EditTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(NEGATIVE_TEMPLATE_URL, {
      ...editTemplate,
    });
    return response;
  };

  static toggleTemplateIsActive = async (editTemplate: EditTemplate): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.patch(TOGGLE_TEMPLATE_IS_ACTIVE_URL, {
      ...editTemplate,
    });
    return response;
  };

  static deleteTemplate = async (id: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.delete(`${DELETE_TEMPLATE_URL}/${id}`);
    return response;
  };

  static checkShortcut = async (shortcut: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(CHECK_TEMPLATE_SHORTCUT_URL, {
      params: { shortcut },
    });
    return response;
  };

  static getTemplateVariables = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(GET_TEMPLATE_VARIABLES_URL);
    return response;
  };

  static uploadAttachment = async (
    file: File,
    options?: { signal?: AbortSignal; onProgress?: (percent: number) => void }
  ): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append("file", file);

    const response: AxiosResponse = await baseAPI.post(UPLOAD_TEMPLATE_ATTACHMENT_URL, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      signal: options?.signal,
      onUploadProgress: (event) => {
        if (!options?.onProgress || !event.total) return;
        options.onProgress(Math.round((event.loaded * 100) / event.total));
      },
    });
    return response;
  };
}

export default Template;
